import type { OutputLayer } from '@/components/preview/output-layer-preview';
import { parseImageSequenceUrls } from '@/components/preview/preview-presentation-slides-layer.helpers';

const MAX_WARMED_URLS = 200;
const warmedUrls = new Set<string>();

const VIDEO_COMPONENT_IDS = new Set(['video', 'backgroundVideo']);

/** Head of the file to buffer — enough to start instantly while the remainder streams. */
export const WARM_VIDEO_TARGET_SECONDS = 5;
/** Give up on sources that never report a buffered range. */
const WARM_VIDEO_TIMEOUT_MS = 30_000;

function isWarmableUrl(url: unknown): url is string {
  return typeof url === 'string' && url.length > 0 && !url.startsWith('data:');
}

/** Still images the preview paints first, including the opening slide of a deck. */
export function collectWarmablePreviewImageUrls(layers: OutputLayer[]): string[] {
  const urls: string[] = [];
  for (const layer of layers) {
    if (layer.visible === false || VIDEO_COMPONENT_IDS.has(layer.componentId)) {
      continue;
    }
    const data = layer.data ?? {};
    const still = data.src || data.imageUrl || data.url;
    if (isWarmableUrl(still)) {
      urls.push(still);
    }
    const [firstSlide] = parseImageSequenceUrls(data);
    if (firstSlide) {
      urls.push(firstSlide);
    }
  }
  return [...new Set(urls)];
}

/** Video sources the preview will play once this cue goes on air. */
export function collectWarmablePreviewVideoUrls(layers: OutputLayer[]): string[] {
  const urls: string[] = [];
  for (const layer of layers) {
    if (layer.visible === false || !VIDEO_COMPONENT_IDS.has(layer.componentId)) {
      continue;
    }
    const data = layer.data ?? {};
    const url = data.videoUrl || data.url;
    if (isWarmableUrl(url)) {
      urls.push(url);
    }
  }
  return [...new Set(urls)];
}

function bufferedSeconds(video: HTMLVideoElement): number {
  const { buffered } = video;
  return buffered.length > 0 ? buffered.end(buffered.length - 1) : 0;
}

/**
 * Buffer the head of a video into the browser cache, then stop fetching.
 *
 * The element is deliberately never attached to the document. Dropping a source is the only way to
 * stop a media element from downloading, and it clears whatever frame the element was painting — so
 * only an element nobody can see is free to do it. The bytes stay in the HTTP cache, which is what
 * lets On Air start from a warm read on the next take instead of a cold fetch.
 */
function warmVideoHead(url: string): void {
  const video = document.createElement('video');
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;

  let stopped = false;

  const stop = () => {
    if (stopped) {
      return;
    }
    stopped = true;
    window.clearTimeout(timeoutId);
    video.removeEventListener('progress', onProgress);
    video.removeEventListener('suspend', onSuspend);
    video.removeEventListener('canplaythrough', stop);
    video.removeEventListener('error', stop);
    video.removeAttribute('src');
    video.load();
  };

  const onProgress = () => {
    if (bufferedSeconds(video) >= WARM_VIDEO_TARGET_SECONDS) {
      stop();
    }
  };

  // The browser stopped fetching on its own — it already has what it wants.
  const onSuspend = () => {
    if (bufferedSeconds(video) > 0) {
      stop();
    }
  };

  video.addEventListener('progress', onProgress);
  video.addEventListener('suspend', onSuspend);
  video.addEventListener('canplaythrough', stop);
  video.addEventListener('error', stop);

  const timeoutId = window.setTimeout(stop, WARM_VIDEO_TIMEOUT_MS);
  video.src = url;
}

function markWarmed(url: string): boolean {
  if (warmedUrls.has(url)) {
    return false;
  }
  if (warmedUrls.size >= MAX_WARMED_URLS) {
    warmedUrls.clear();
  }
  warmedUrls.add(url);
  return true;
}

/** Warm the browser cache so the next cue paints and plays without waiting on the network. */
export function warmPreviewLayerMedia(layers: OutputLayer[]): void {
  if (typeof window === 'undefined') {
    return;
  }
  for (const url of collectWarmablePreviewImageUrls(layers)) {
    if (markWarmed(url)) {
      const image = new Image();
      image.decoding = 'async';
      image.src = url;
    }
  }
  for (const url of collectWarmablePreviewVideoUrls(layers)) {
    if (markWarmed(url)) {
      warmVideoHead(url);
    }
  }
}
